import { ReactNode, forwardRef, HTMLAttributes, useState, useEffect, useRef } from 'react';
import { useResponsive } from '@/hooks/useResponsive';

interface ViewProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: ReactNode;
}

interface DesktopViewProps extends ViewProps {
  position?: 'left' | 'right';
  isOpen?: boolean;
}

/**
 * Desktop view (>= 1024px)
 * Renders with layout styling for desktop layout
 * @param position - Position the view on 'left' or 'right' side (default: 'left')
 * @param isOpen - Toggle to animate view in/out (default: true)
 */
export const DesktopView = forwardRef<HTMLDivElement, DesktopViewProps>(({ className = '', children, position = 'left', isOpen = true, ...props }, ref) => {
  const { isDesktop } = useResponsive();
  const internalRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [width, setWidth] = useState(400);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!internalRef.current) return;

      const rect = internalRef.current.getBoundingClientRect();
      let newWidth: number;

      if (position === 'right') {
        newWidth = rect.right - e.clientX;
      } else {
        newWidth = e.clientX - rect.left;
      }

      // Constrain width between 250px and 800px
      newWidth = Math.max(250, Math.min(800, newWidth));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, position, setWidth]);

  const handleResizeStart = () => {
    setIsResizing(true);
  };

  if (!isDesktop) return null;

  const positionClasses = position === 'right'
    ? 'right-0 left-auto'
    : 'left-0 right-auto';

  const translateClasses = !isOpen
    ? position === 'right' ? 'translate-x-full'
      : '-translate-x-full'
    : 'translate-x-0';

  return (
    <div
      ref={(node) => {
        internalRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      className={`
        fixed bottom-0 ${positionClasses} flex flex-col justify-end lg:justify-start max-h-full lg:gap-5 h-dvh lg:h-full h-full z-20 select-none transition-transform duration-300 ease-in-out ${translateClasses}
        ${position === 'right' ? 'border-l' : 'border-r'}
        ${isResizing ? '' : 'transition-all duration-200'}
        ${className}
      `}
      style={{ width: `${width}px` }}
      {...props}
    >
      {/* Resize handle */}
      <div
        onMouseDown={handleResizeStart}
        className={`
          absolute top-0 bottom-0 w-1 hover:w-1.5 hover:bg-secondary transition-all
          ${isResizing ? 'w-1.5 bg-secondary cursor-grabbing' : 'cursor-col-resize'}
          ${position === 'right' ? 'left-0' : 'right-0'}
        `}
      />

      {children}
    </div>
  );
});

DesktopView.displayName = 'DesktopView';
